import * as React from 'react';
import { Badge } from './Badge';
import { Inline } from './Layout';
import { PickerMenu, PickerMenuOption, type PickerMenuProps } from './Picker';
import type { StatusTone } from './status-tone';
import { Text } from './Text';

export interface StatusPickerStatus {
  value: string;
  label: string;
  /** The badge colour for this status. Neutral when left off. */
  tone?: StatusTone;
}

export interface StatusPickerProps
  extends Omit<PickerMenuProps, 'trigger' | 'children' | 'prefix'> {
  /** The statuses that can be chosen, in the order they happen. */
  statuses: StatusPickerStatus[];
  /** The current status. Need not be one of `statuses` — see `valueLabel`. */
  value: string;
  /**
   * How to show a value that is not in the list — a terminal status the
   * picker cannot set, like GOA. Falls back to the raw value.
   */
  valueLabel?: string;
  valueTone?: StatusTone;
  onValueChange: (value: string) => void;
  /** Words before the badge on the closed control. */
  prefix?: React.ReactNode;
  /**
   * A change is saving: the options grey out and the trigger will not open,
   * but the menu stays where it is so the screen can close it when the save
   * settles.
   */
  busy?: boolean;
}

/**
 * A PickerMenu over a list of statuses, each drawn as a dotted Badge in its
 * own tone — the call's status on the profile page.
 *
 *   <StatusPicker
 *     statuses={STATUSES}
 *     value={call.status}
 *     onValueChange={change}
 *     open={open}
 *     onOpenChange={setOpen}
 *     busy={isSaving}
 *   />
 *
 * Like ReasonPicker, the screen owns the open state: a choice may need a
 * request, or a follow-up dialog, before the menu should close.
 */
export const StatusPicker = React.forwardRef<HTMLDivElement, StatusPickerProps>(
  function StatusPicker(
    {
      statuses,
      value,
      valueLabel,
      valueTone,
      onValueChange,
      prefix = 'Status:',
      busy = false,
      disabled = false,
      onOpenChange,
      ...props
    },
    ref
  ) {
    const current = statuses.find((status) => status.value === value);
    const label = current?.label ?? valueLabel ?? value;
    const tone = current?.tone ?? valueTone ?? 'neutral';
    return (
      <PickerMenu
        ref={ref}
        width="sm"
        disabled={disabled || busy}
        onOpenChange={(open) => {
          if (!disabled && !busy) onOpenChange(open);
        }}
        trigger={
          <Inline gap={2} align="center">
            {prefix ? (
              <Text variant="body-sm" as="span" tone="muted">
                {prefix}
              </Text>
            ) : null}
            <Badge tone={tone} dot>
              {label}
            </Badge>
          </Inline>
        }
        {...props}
      >
        {statuses.map((status) => (
          <PickerMenuOption
            key={status.value}
            onClick={() => onValueChange(status.value)}
            disabled={busy}
            selected={value === status.value}
          >
            <Badge tone={status.tone ?? 'neutral'} dot>
              {status.label}
            </Badge>
          </PickerMenuOption>
        ))}
      </PickerMenu>
    );
  }
);
